import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import { uploadImageToS3, deleteImageFromS3, getFileExtension } from "../../utils/s3Upload";
import { getRole } from "../../utils/role";

const FollowUpStage1 = ({ reportId, initialData = null, onSuccess }) => {
  const [cropsList, setCropsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState({});

  const [formData, setFormData] = useState({
    is_same_crop: "Yes",
    registered_crop_name: "",
    alternate_crop_name: "",
    farm_size: "",
    farm_size_unit: "Acres",
    seed_total_quantity: "",
    seed_quantity_unit: "kg",
    seed_total_cost_rs: "",
    input_costs: [
      { name: "Urea", quantity: "", unit: "Bag", price_per_unit: "", total_cost: "", is_fixed: true },
      { name: "DAP", quantity: "", unit: "Bag", price_per_unit: "", total_cost: "", is_fixed: true }
    ],
    input_cost_receipt_urls: [],
    has_disease_issue: "No",
    disease_details: "",
    disease_photo_urls: [],
    last_season_yield_quintal: "",
    last_season_selling_price_rs: "",
    remarks: ""
  });

  const isReadOnly = Boolean(initialData && Object.keys(initialData).length > 0) && getRole() !== 'SUPPORT';

  const farmSizeUnits = [
    "Acres", "Hectare", "Bigha", "Gaj", "Kanal", "Biswa",
    "Dismil", "Killa", "Marla", "Nali", "Guntha",
    "Square Karam", "Ankanam", "Gajam", "Chatak",
    "Dhur", "Perch", "Lessa", "Murabba", "Biswa Kacha", "Pura", "sq mtr"
  ];

  useEffect(() => {
    const initComponent = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get("/api/v2/recommendations/crops");
        if (res.data && Array.isArray(res.data.data)) {
          setCropsList(res.data.data);
        } else {
          setCropsList(["Wheat", "Paddy", "Cotton", "Sugarcane", "Vegetables", "Pulses"]);
        }
      } catch (err) {
        console.warn("Could not fetch crops list, using fallback:", err);
        setCropsList(["Wheat", "Paddy", "Cotton", "Sugarcane", "Vegetables", "Pulses"]);
      } finally {
        if (initialData && Object.keys(initialData).length > 0) {
          const sanitizedData = { ...initialData };

          if (typeof sanitizedData.input_cost_receipt_url === "string") {
            sanitizedData.input_cost_receipt_urls = [sanitizedData.input_cost_receipt_url];
          } else if (!Array.isArray(sanitizedData.input_cost_receipt_urls)) {
            sanitizedData.input_cost_receipt_urls = [];
          }

          if (typeof sanitizedData.disease_photo_url === "string") {
            sanitizedData.disease_photo_urls = [sanitizedData.disease_photo_url];
          } else if (!Array.isArray(sanitizedData.disease_photo_urls)) {
            sanitizedData.disease_photo_urls = [];
          }

          setFormData(sanitizedData);
        }
        setLoading(false);
      }
    };

    initComponent();
  }, [initialData]);

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTableChange = (index, field, value) => {
    if (isReadOnly) return;
    const updatedTable = formData.input_costs.map((row, i) => {
      if (i !== index) return row;
      const updatedRow = { ...row, [field]: value };

      if (field === "quantity" || field === "price_per_unit") {
        const qtyRaw = field === "quantity" ? value : updatedRow.quantity;
        const priceRaw = field === "price_per_unit" ? value : updatedRow.price_per_unit;

        if (String(qtyRaw).toUpperCase() === "NA" || String(priceRaw).toUpperCase() === "NA") {
          updatedRow.total_cost = "NA";
        } else {
          const qty = parseFloat(qtyRaw) || 0;
          const price = parseFloat(priceRaw) || 0;
          updatedRow.total_cost = qty > 0 && price > 0 ? (qty * price).toFixed(2) : "";
        }
      }
      return updatedRow;
    });
    handleChange("input_costs", updatedTable);
  };

  const addTableRow = () => {
    handleChange("input_costs", [
      ...formData.input_costs,
      { name: "", quantity: "", unit: "Bag", price_per_unit: "", total_cost: "", is_fixed: false }
    ]);
  };

  const removeTableRow = (index) => {
    handleChange("input_costs", formData.input_costs.filter((_, i) => i !== index));
  };

  const handleMultiPhotoUpload = async (fieldKey, fileList) => {
    if (isReadOnly || !fileList || fileList.length === 0) return;
    const files = Array.from(fileList);

    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      const uploadPromises = files.map(async (file) => {
        const fileExt = getFileExtension(file);
        return await uploadImageToS3(file, fileExt, "follow-up-photos");
      });

      const newS3Urls = await Promise.all(uploadPromises);
      const existingUrls = formData[fieldKey] || [];
      handleChange(fieldKey, [...existingUrls, ...newS3Urls]);
    } catch (err) {
      console.error("S3 Multi-Upload Error:", err);
      alert("Failed to upload one or more photos to S3. Please check your connection.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handlePhotoRemove = async (fieldKey, indexToRemove) => {
    if (isReadOnly) return;
    const urlsArray = formData[fieldKey] || [];
    const fileUrl = urlsArray[indexToRemove];
    if (!fileUrl) return;

    try {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: true }));
      await deleteImageFromS3(fileUrl);
      
      const updatedUrls = urlsArray.filter((_, i) => i !== indexToRemove);
      handleChange(fieldKey, updatedUrls);
    } catch (err) {
      console.error("S3 Deletion Error:", err);
      alert("Could not remove photo from AWS server.");
    } finally {
      setUploadingMedia((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    if (Object.values(uploadingMedia).some((up) => up)) {
      alert("Please wait for all photo uploads to finish uploading before submitting.");
      return;
    }

    const hasValidCostRow = formData.input_costs.some(row => 
      parseFloat(row.total_cost) > 0 || String(row.total_cost).toUpperCase() === "NA"
    );
    
    if (!hasValidCostRow) {
      alert("Input Cost Breakdown is required. Please fill in the quantity and price for Urea, DAP, or another input (Type 'NA' if not available).");
      return;
    }

    try {
      setSubmitting(true);
      await axiosInstance.post(`/follow-up/response/${reportId}`, {
        follow_up_stage: 1,
        answers: formData
      });

      alert("Stage 1 Baseline submitted successfully!");
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit Stage 1.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderMultiFilePreview = (fieldKey, urls = []) => {
    if (urls.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2.5">
        {urls.map((url, idx) => (
          <div key={idx} className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-md border border-purple-200 text-xs shadow-sm max-w-full">
            <span className="text-green-600 font-bold shrink-0">✓</span>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline truncate max-w-[140px] sm:max-w-[200px]"
              title={url}
            >
              {url.split("/").pop()}
            </a>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => handlePhotoRemove(fieldKey, idx)}
                className="text-red-500 hover:text-red-700 font-bold ml-1 p-0.5 shrink-0"
                title="Delete photo"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="p-6 text-center text-gray-500 text-sm">Loading Stage 1 baseline...</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-left">

      {isReadOnly && (
        <div className="bg-blue-50 text-blue-800 text-xs sm:text-sm p-3 rounded-md border border-blue-200 font-medium flex items-center gap-2">
          🔒 This form has already been submitted and is locked for editing.
        </div>
      )}

      {/* SECTION 1: CROP & FARM BASELINE */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          1. Crop & Farm Baseline
        </h4>

        {/* Q1 & Q2 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Is Growing the Same Registered Crop? <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.is_same_crop}
              onChange={(e) => handleChange("is_same_crop", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Crop Name <span className="text-red-500">*</span>
            </label>
            {formData.is_same_crop === "Yes" ? (
              <select
                value={formData.registered_crop_name}
                onChange={(e) => handleChange("registered_crop_name", e.target.value)}
                disabled={isReadOnly}
                required
                className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
              >
                <option value="" disabled>Select crop from list...</option>
                {cropsList.map((crop) => (
                  <option key={crop.id} value={crop.name_en}>
                    {crop.name_en}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={formData.alternate_crop_name}
                onChange={(e) => handleChange("alternate_crop_name", e.target.value)}
                disabled={isReadOnly}
                required
                placeholder="Type actual crop name grown..."
                className="w-full border border-purple-300 bg-purple-50/20 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
              />
            )}
          </div>
        </div>

        {/* Q3: Farm Size */}
        <div className="w-full sm:w-1/2">
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
            Farm Size <span className="text-red-500">*</span>
          </label>
          <div className="flex">
            <input
              type="number"
              step="any"
              value={formData.farm_size}
              onChange={(e) => handleChange("farm_size", e.target.value)}
              disabled={isReadOnly}
              required
              placeholder="e.g., 5.5"
              className="w-full border border-gray-300 rounded-l-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
            />
            <select
              value={formData.farm_size_unit}
              onChange={(e) => handleChange("farm_size_unit", e.target.value)}
              disabled={isReadOnly}
              className="bg-gray-100 border-t border-b border-r border-gray-300 text-xs font-semibold text-gray-700 rounded-r-md px-2 focus:outline-none"
            >
              {farmSizeUnits.map((unit) => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Q4: Seed Details */}
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            Seed Details <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="block text-xs text-gray-500 mb-1">Total quantity of seed/seedling</span>
              <div className="flex">
                <input
                  type="number"
                  step="any"
                  value={formData.seed_total_quantity}
                  onChange={(e) => handleChange("seed_total_quantity", e.target.value)}
                  disabled={isReadOnly}
                  required
                  placeholder="e.g., 20"
                  className="w-full border border-gray-300 rounded-l-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
                />
                <select
                  value={formData.seed_quantity_unit}
                  onChange={(e) => handleChange("seed_quantity_unit", e.target.value)}
                  disabled={isReadOnly}
                  className="bg-gray-100 border-t border-b border-r border-gray-300 text-xs font-semibold text-gray-700 rounded-r-md px-2 focus:outline-none"
                >
                  <option value="nos">nos</option>
                  <option value="kg">kg</option>
                  <option value="gm">gm</option>
                </select>
              </div>
            </div>
            <div>
              <span className="block text-xs text-gray-500 mb-1">Total Seed Cost (₹)</span>
              <input
                type="number"
                step="any"
                value={formData.seed_total_cost_rs}
                onChange={(e) => handleChange("seed_total_cost_rs", e.target.value)}
                disabled={isReadOnly}
                required
                placeholder="e.g., 1500"
                className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: INPUT COST BREAKDOWN */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2 flex items-center justify-between">
          <span>2. Previous Season Input Cost Breakdown</span>
          <span className="text-[10px] sm:text-xs text-gray-400 normal-case font-normal">(Enter "NA" if data unavailable)</span>
        </h4>

        {/* Q5 */}
        <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
          <table className="w-full text-left border-collapse min-w-[550px]">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300 text-[11px] sm:text-xs uppercase text-gray-700">
                <th className="p-2">Name of Input</th>
                <th className="p-2 w-20 sm:w-24">Quantity</th>
                <th className="p-2 w-20 sm:w-24">Unit</th>
                <th className="p-2 w-24 sm:w-28">Price/Unit (₹)</th>
                <th className="p-2 w-24 sm:w-28">Total Cost (₹)</th>
                <th className="p-2 text-center w-10 sm:w-12">Del</th>
              </tr>
            </thead>
            <tbody>
              {formData.input_costs.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="p-1 sm:p-1.5">
                    <input
                      type="text"
                      value={row.name}
                      readOnly={row.is_fixed}
                      onChange={(e) => handleTableChange(idx, "name", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="e.g., Potash"
                      className={`w-full border rounded p-1.5 text-xs sm:text-sm ${row.is_fixed ? "bg-gray-100 font-semibold cursor-not-allowed" : ""}`}
                    />
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <input
                      type="text"
                      value={row.quantity}
                      onChange={(e) => handleTableChange(idx, "quantity", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="0 or NA"
                      className="w-full border rounded p-1.5 text-xs sm:text-sm"
                    />
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <select
                      value={row.unit}
                      onChange={(e) => handleTableChange(idx, "unit", e.target.value)}
                      disabled={isReadOnly}
                      className="w-full border rounded p-1.5 text-xs sm:text-sm bg-white"
                    >
                      <option value="kg">kg</option>
                      <option value="Bag">Bag</option>
                    </select>
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <input
                      type="text"
                      value={row.price_per_unit}
                      onChange={(e) => handleTableChange(idx, "price_per_unit", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="0.00 or NA"
                      className="w-full border rounded p-1.5 text-xs sm:text-sm"
                    />
                  </td>
                  <td className="p-1 sm:p-1.5">
                    <input
                      type="text"
                      value={row.total_cost}
                      onChange={(e) => handleTableChange(idx, "total_cost", e.target.value)}
                      disabled={isReadOnly}
                      placeholder="Auto"
                      className="w-full border border-purple-200 bg-purple-50/30 rounded p-1.5 text-xs sm:text-sm font-semibold text-purple-900"
                    />
                  </td>
                  <td className="p-1 sm:p-1.5 text-center">
                    {!row.is_fixed ? (
                      <button
                        type="button"
                        onClick={() => removeTableRow(idx)}
                        disabled={isReadOnly}
                        className="text-red-500 hover:text-red-700 font-bold px-1.5 py-0.5 rounded hover:bg-red-50 text-sm"
                      >
                        ✕
                      </button>
                    ) : (
                      <span className="text-gray-300 text-xs" title="Required input">🔒</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            onClick={addTableRow}
            disabled={isReadOnly}
            className="mt-3 w-full sm:w-auto text-xs font-semibold bg-purple-50 text-purple-700 px-3 py-2 sm:py-1.5 rounded-md border border-purple-200 hover:bg-purple-100 transition"
          >
            + Add Custom Input / Expense
          </button>
        </div>

        {/* Optional Multi-Media Upload: Receipts */}
        <div className="bg-purple-50/40 border border-dashed border-purple-200 p-3 sm:p-4 rounded-md">
          <label className="block text-xs font-semibold text-purple-900 mb-1.5">
            📷 Optional: Upload Bill or Receipt Photos (Multiple allowed)
          </label>
          
          <input
            type="file"
            multiple
            accept="image/*,.pdf"
            disabled={uploadingMedia.input_cost_receipt_urls || isReadOnly}
            onChange={(e) => handleMultiPhotoUpload("input_cost_receipt_urls", e.target.files)}
            className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-purple-100 file:text-purple-700 file:font-semibold hover:file:bg-purple-200 cursor-pointer disabled:opacity-50"
          />

          {uploadingMedia.input_cost_receipt_urls && (
            <div className="text-xs text-purple-600 font-medium mt-2 flex items-center gap-2">
              <span className="inline-block w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>
              Uploading photos to AWS S3...
            </div>
          )}

          {renderMultiFilePreview("input_cost_receipt_urls", formData.input_cost_receipt_urls)}
        </div>
      </div>

      {/* SECTION 3: DISEASE & YIELD HISTORY */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm space-y-4">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b pb-2">
          3. Disease & Yield History
        </h4>

        {/* Q6: Disease / Pest Issue */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Last Season Disease or Pest Issue? <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.has_disease_issue}
              onChange={(e) => handleChange("has_disease_issue", e.target.value)}
              disabled={isReadOnly}
              className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>

          {formData.has_disease_issue === "Yes" && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Disease / Pest Name & Symptoms</label>
              <textarea
                rows={2}
                value={formData.disease_details}
                onChange={(e) => handleChange("disease_details", e.target.value)}
                disabled={isReadOnly}
                required
                placeholder="Specify disease name or symptoms..."
                className="w-full border border-purple-300 bg-purple-50/20 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}

          {/* Optional Multi-Media Upload: Disease Photos */}
          <div className="bg-purple-50/40 border border-dashed border-purple-200 p-3 sm:p-4 rounded-md">
            <label className="block text-xs font-semibold text-purple-900 mb-1.5">
              📷 Optional: Upload Crop Disease Photos (Multiple allowed)
            </label>
            
            <input
              type="file"
              multiple
              accept="image/*"
              disabled={uploadingMedia.disease_photo_urls || isReadOnly}
              onChange={(e) => handleMultiPhotoUpload("disease_photo_urls", e.target.files)}
              className="w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-purple-100 file:text-purple-700 file:font-semibold hover:file:bg-purple-200 cursor-pointer disabled:opacity-50"
            />

            {uploadingMedia.disease_photo_urls && (
              <div className="text-xs text-purple-600 font-medium mt-2 flex items-center gap-2">
                <span className="inline-block w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></span>
                Uploading photos to AWS S3...
              </div>
            )}

            {/* Render Multi-File Previews */}
            {renderMultiFilePreview("disease_photo_urls", formData.disease_photo_urls)}
          </div>
        </div>

        {/* Last Season Yield & Selling Price */}
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            Last Season Yield & Market Selling Price <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <span className="block text-xs text-gray-500 mb-1">Total Yield (in Quintals)</span>
              <input
                type="text"
                value={formData.last_season_yield_quintal}
                onChange={(e) => handleChange("last_season_yield_quintal", e.target.value)}
                disabled={isReadOnly}
                required
                placeholder="e.g., 45 or NA"
                className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm"
              />
            </div>
            <div>
              <span className="block text-xs text-gray-500 mb-1">Market Selling Price (₹ per Quintal)</span>
              <input
                type="text"
                step="any"
                value={formData.last_season_selling_price_rs}
                onChange={(e) => handleChange("last_season_selling_price_rs", e.target.value)}
                disabled={isReadOnly}
                required
                placeholder="e.g., 2150 or NA"
                className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 4: GENERAL REMARKS */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100 shadow-sm">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
          General Remarks / Additional Field Notes
        </label>
        <textarea
          rows={3}
          value={formData.remarks || ""}
          onChange={(e) => handleChange("remarks", e.target.value)}
          disabled={isReadOnly}
          placeholder="Add any extra observations, farmer feedback, or notes here..."
          className="w-full border border-gray-300 rounded-md p-2.5 sm:p-2 text-sm focus:ring-2 focus:ring-purple-500 bg-white"
        />
      </div>

      {!isReadOnly && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={submitting || Object.values(uploadingMedia).some((up) => up)}
            className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-md hover:bg-purple-700 disabled:opacity-50 transition shadow-sm"
          >
            {submitting ? "Saving Stage 1..." : initialData ? "Update Stage 1" : "Submit Stage 1"}
          </button>
        </div>
      )}
    </form>
  );
};

export default FollowUpStage1;